import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { STATE_ZIP_CODES } from "./ZipCodesByState.js";
import * as Sentry from "@sentry/react";
import { getBuildMeta } from "../buildMeta.js";
import moment from "moment";

export function userHasSwitchCreds(user) {
  if (!user) {
    return false;
  } else {
    if (
      user?.officer?.deptAttribute?.nletsPassword &&
      user?.officer?.deptAttribute?.nletsDevId?.toString() &&
      user?.officer?.deptAttribute?.nletsOri &&
      user?.officer?.deptAttribute?.nletsLogin
    ) {
      return true;
    } else {
      return false;
    }
  }
}
export function isRaceDataPresent(ticket) {
  return (
    !!ticket?.perceivedRace?.id ||
    !!ticket?.ticketStopType?.id ||
    !!ticket?.ticketSearchType?.id ||
    !!ticket?.ticketEvidenceType?.id ||
    !!ticket?.ticketOutcomeType?.id ||
    !!ticket?.passengerTicketEvidenceType?.id ||
    !!ticket?.passengerTicketOutcomeType?.id ||
    !!ticket?.passengerTicketSearchType?.id
    // ||
    // !!ticket?.uofInjuryDetails?.length > 0 ||
    // !!ticket?.uofInstance?.id ||
    // !!ticket?.uofInstanceTypes?.length > 0
  );
}
export function isPassengerRaceDataPresent(ticket) {
  return (
    !!ticket?.passengerTicketEvidenceType?.id ||
    !!ticket?.passengerTicketOutcomeType?.id ||
    !!ticket?.passengerTicketSearchType?.id
  );
}

export function hasAllRequiredRaceFields(ticket) {
  return (
    !!ticket?.perceivedRace?.id &&
    !!ticket?.ticketStopType?.id &&
    !!ticket?.ticketSearchType?.id &&
    !!ticket?.ticketEvidenceType?.id &&
    !!ticket?.ticketOutcomeType?.id
  );
}

const NEW_SETUP_PROPS = {
  callPriorities: "id",
};

export function isSetupMissingProp(setupLists) {
  let res = false;

  Object.keys(NEW_SETUP_PROPS).every((key) => {
    const setupListArr = setupLists?.[key];
    const setupListHasProp = setupListArr?.[0] != null && Object.prototype.hasOwnProperty.call(
      setupListArr[0],
      NEW_SETUP_PROPS[key],
    );
    if (!setupListArr || !setupListHasProp) {
      res = true;
      return false;
    } else {
      return true;
    }
  });
  return res;
}

export function useLocalStorage(key, initialValue, addTimestamp = true) {
  const [storedValue, setStoredValue] = useState(() => {
    if (typeof window === "undefined") {
      return initialValue;
    }
    try {
      let item = localGetData(key);
      if (item) {
        return item;
      } else {
        localStoreData(initialValue, key, addTimestamp);
        return initialValue;
      }
    } catch (error) {
      console.error(
        `You may have decompressed data in local storage for key ${key}. useLocalStorage error:`,
        error,
      );
      return initialValue;
    }
  });

  const setValue = value => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      if (typeof window !== "undefined" && window.LZString !== undefined) {
        window.localStorage.setItem(
          key,
          window.LZString.compress(JSON.stringify(valueToStore)),
        );
      }
    } catch (error) {
      console.log("setValue error:", error);
    }
  };

  return [storedValue, setValue];
}

export function removeFromLocalStorage(key) {
  if (typeof window !== "undefined") {
    try {
      // Remove item from local storage by key
      window.localStorage.removeItem(key);
    } catch (error) {
      // Handle the error case
      console.log("removeFromLocalStorage error:", error);
    }
  }
}

export const parseTime = timeString => {
  let time = timeString ? timeString.replace(/-/g, " ") : null;
  return time;
};

export const numberSort = (direction, id, data, path = null) => {
  const rows = Array.isArray(data) ? data : [];
  let result = rows.slice().sort((a, b) => {
    let first = objectValueByString(a, path ? path : lookUpPath[id]) || 0;
    let second = objectValueByString(b, path ? path : lookUpPath[id]) || 0;
    if (direction === "asc") {
      return first - second;
    } else {
      return second - first;
    }
  });
  return result;
};
const lookUpPath = {
  type: "classification.name",
  secondaryAddress: "officerIncidentSpecifics[0].secondaryAddress",
  assignment: "officerIncidentSpecifics[0].officer.deptAttribute.assignment",
  incidentNumber: "incidentNumber",
  badge: "officerIncidentSpecifics[0].officer.deptAttribute.badge",
};

export const generalSort = (direction, id, data) => {
  if (!Array.isArray(data)) return data;
  let result = data.slice().sort();
  return result;
};

export const untrim = (string, char) => {
  if (!char) {
    char = "";
  }
  return string ? string + char + " " : "";
};

export const parseAddressName = addressObj => {
  if (!addressObj) return "";
  return `${untrim(addressObj?.streetNumber)}${untrim(addressObj?.streetName)}${
    addressObj?.unit ? "#" + untrim(addressObj.unit) : ""
  }${
    addressObj?.streetNumber || addressObj?.streetName || addressObj?.unit
      ? ", "
      : ""
  }${untrim(addressObj?.city, ",")}${untrim(addressObj.state, ",")}${
    addressObj.zip
  }`;
};
export const parseAddressNameForDashboard = addressObj => {
  let str = `${untrim(addressObj?.streetNumber)}${
    addressObj?.streetName || ""
  }${addressObj?.unit ? "#" + untrim(addressObj.unit) : ""}${
    addressObj?.streetNumber || addressObj?.streetName || addressObj?.unit
      ? ", "
      : ""
  }${addressObj?.city || ""}`;

  return str;
};

export const wordSort = (direction, id, data, path = null) => {
  const rows = Array.isArray(data) ? data : [];
  let result = rows.slice().sort((a, b) => {
    let first =
      objectValueByString(a, path ? path : lookUpPath[id])?.toUpperCase() || "";
    let second =
      objectValueByString(b, path ? path : lookUpPath[id])?.toUpperCase() || "";
    if (first < second) {
      if (direction === "asc") {
        return -1;
      } else {
        return 1;
      }
    }
    if (first > second) {
      if (direction === "asc") {
        return 1;
      } else {
        return -1;
      }
    }
    return 0;
  });

  return result;
};

export const dateSort = (direction, id, data, path = null) => {
  const rows = Array.isArray(data) ? data : [];
  let result = rows.slice().sort((a, b) => {
    let first =
      moment(
        objectValueByString(a, path ? path : lookUpPath[id]),
        "YYYY-MM-DD HH:mm:ss UTC",
      ).unix() || 0;
    let second =
      moment(
        objectValueByString(b, path ? path : lookUpPath[id]),
        "YYYY-MM-DD HH:mm:ss UTC",
      ).unix() || 0;
    if (direction === "asc") {
      return second - first;
    } else {
      return first - second;
    }
  });
  return result;
};

export const incidentNumberSort = (direction, id, data, path = null) => {
  const rows = Array.isArray(data) ? data : [];
  let result = rows.slice().sort((a, b) => {
    let firstNumString =
      objectValueByString(a, path ? path : lookUpPath[id])?.slice(-6) || 0;
    let secondNumString =
      objectValueByString(b, path ? path : lookUpPath[id])?.slice(-6) || 0;
    let firstNum = parseInt(firstNumString);
    let secondNum = parseInt(secondNumString);
    if (direction === "asc") {
      return firstNum - secondNum;
    } else {
      return secondNum - firstNum;
    }
  });
  return result;
};

export const parseObjectArrayToIdArray = arr => {
  if (!Array.isArray(arr) || arr.length === 0) {
    return [];
  }

  return arr
    .map(obj => obj?.id)
    .filter(id => id !== undefined && id !== null)
    .map(id => id.toString());
};

export const parseObjectArrayIdString = arr => {
  if (!Array.isArray(arr) || arr.length === 0) {
    return "";
  }

  return arr
    .map(obj => obj?.id)
    .filter(id => id !== undefined && id !== null)
    .map(id => id.toString())
    .join(",");
};

export const parseNestedObjectArrayToIdArray = (arr, key) => {
  let idArr = [];
  if (Array.isArray(arr) && arr.length > 0) {
    arr.forEach(obj => {
      const id = obj?.[key]?.id;
      if (id !== undefined && id !== null) {
        idArr.push(id.toString());
      }
    });
  }
  return idArr;
};

export const formatName = (firstName = "", middleName = "", lastName = "") => {
  if (!firstName) {
    firstName = "";
  }
  if (!lastName) {
    lastName = "";
  }
  if (!middleName) {
    middleName = "";
  }
  let formattedName = `${lastName !== "" ? lastName + ", " : ""}${
    firstName !== "" ? firstName + " " : ""
  }${middleName}`;
  return formattedName;
};

export const formatOfficerName = officer => {
  if (!officer) {
    return "";
  } else if (
    !officer?.personalAttribute &&
    !officer?.deptAttribute &&
    officer?.name
  ) {
    return officer?.name;
  } else if (officer) {
    let abbreviation = officer?.deptAttribute
      ? officer?.deptAttribute?.ori?.abbreviation + "-"
      : "";
    let badge = officer?.deptAttribute
      ? officer?.deptAttribute?.badge + ": "
      : "";
    let firstName = officer?.personalAttribute
      ? officer?.personalAttribute?.firstName
      : "";
    let lastName = officer?.personalAttribute
      ? officer?.personalAttribute?.lastName + ", "
      : "";
    return `${abbreviation}${badge}${lastName}${firstName}`;
  } else {
    return "";
  }
};

export const formatOfficerOriBadge = officer => {
  if (!officer) {
    return "";
  } else if (officer) {
    let abbreviation = officer?.deptAttribute
      ? officer?.deptAttribute?.ori?.abbreviation + "-"
      : "";
    let badge = officer?.deptAttribute ? officer?.deptAttribute?.badge : "";
    return `${abbreviation}${badge}`;
  } else {
    return "";
  }
};

export const formatNameFromPersonObj = person => {
  if (!person) {
    return "";
  } else if (!person?.personalAttribute && person?.name) {
    return person.name;
  } else if (person?.personalAttribute) {
    const firstName = person.personalAttribute?.firstName;
    const middleName = person.personalAttribute?.middleName;
    const lastName = person.personalAttribute?.lastName;
    const formattedName = `${lastName ? lastName + ", " : ""}${
      firstName ? firstName : ""
    }${middleName ? " " + middleName : ""}`;
    return formattedName;
  } else {
    return "";
  }
};

export const personPanelHeader = (
  person,
  last,
  sealed = false,
  juvenile = false,
  yoe = false,
) => {
  if (!person) {
    return "";
  } else if (!person?.personalAttribute && person?.name) {
    let name = person.name;
    if (sealed) {
      person.name = person.name + "[Sealed]";
    }
    return name;
  } else if (person?.personalAttribute) {
    if (!person?.personalAttribute?.organization) {
      const firstName = person?.personalAttribute?.firstName;
      const lastName = person?.personalAttribute?.lastName;
      const middleInitial = person?.personalAttribute?.middleName?.charAt(0);
      const dob = person?.personalAttribute?.dob
        ? moment
            .utc(person?.personalAttribute?.dob, "YYYY-MM-DD HH:mm:ss UTC")
            .local()
            .format("MM/DD/YYYY")
        : "";
      let formattedName = `${lastName ? lastName + ", " : ""}${
        firstName ? firstName : ""
      }${middleInitial ? " " + middleInitial : ""}${sealed ? "[Sealed]" : ""}${
        dob ? " (" + dob + ")" : ""
      }`;
      if (juvenile) {
        formattedName = formattedName + " [JUV]";
      }
      if (!juvenile && yoe) {
        formattedName = formattedName + " [YOE]";
      }
      if (!last) {
        formattedName = formattedName + "; ";
      }
      return formattedName;
    } else {
      return (
        person?.personalAttribute?.firstName +
        " " +
        person?.personalAttribute?.lastName +
        `${last ? "" : "; "}`
      );
    }
  } else {
    return "";
  }
};

export const formatNameFromVehicleObj = (
  vehicle,
) => {
  if (!vehicle) {
    return "";
  } else if (
    !vehicle?.vehicleMake &&
    !vehicle?.vehicleModel &&
    !vehicle?.vehiclePlate &&
    vehicle.name
  ) {
    return vehicle.name;
  } else if (vehicle) {
    const make = vehicle?.vehicleMake?.name;
    const model = vehicle?.vehicleModel?.name;
    const plate = vehicle?.vehiclePlate?.name;
    const state = vehicle?.vehicleState?.name;
    const year = vehicle?.year;
    const formattedName = `${year ? year + " " : ""}${make ? make + " " : ""}${
      model ? "(" + model + ")-" : ""
    }${state ? state + " " : ""}${plate ? plate : ""}`;
    return formattedName;
  } else {
    return "";
  }
};

export const vehiclePanelHeader = (vehicle, last) => {
  if (!vehicle) {
    return "";
  } else if (
    !vehicle?.vehicleMake &&
    !vehicle?.vehicleModel &&
    !vehicle?.vehiclePlate &&
    vehicle.name
  ) {
    return vehicle.name;
  } else if (vehicle) {
    const make = vehicle?.vehicleMake?.name;
    const model = vehicle?.vehicleModel?.name;
    const plate = vehicle?.vehiclePlate?.name;
    const state = vehicle?.vehicleState?.name;
    const formattedName = `${make ? make + " " : ""}${
      model ? "(" + model + ") - " : ""
    }${state ? state + ": " : ""}${plate ? plate : ""}${last ? "" : "; "}`;
    return formattedName;
  } else {
    return "";
  }
};

export const arrestPanelHeader = (
  arrestee,
  last,
  sealed = false,
  juvenile = false,
  yoe = false,
) => {
  if (!arrestee?.id) {
    return `${last ? "" : "; "}`;
  } else if (arrestee) {
    if (sealed) {
      return `SEALED${last ? "" : "; "}`;
    }
    if (juvenile) {
      return `Juvenile${last ? "" : "; "}`;
    }
    if (yoe) {
      return `YOE${last ? "" : "; "}`;
    }
    if (arrestee?.name) {
      return arrestee?.name;
    }
    const lastName = arrestee?.personalAttribute?.lastName || "";
    const firstName = arrestee?.personalAttribute?.firstName || "";
    const formattedName = `${lastName}, ${firstName}${last ? "" : "; "}${
      sealed ? "[Sealed]" : ""
    }`;
    return formattedName;
  } else {
    return "";
  }
};

export const reviewPanelHeader = incidentReviewObj => {
  //if approvalStatus?.name === "reviewed" return the reviewed header
  //else return the reviewStatus and review date
  if (!incidentReviewObj) return "";
  if (incidentReviewObj?.approvalStatus?.name === "Approved") {
    let time = incidentReviewObj?.reviewDate
      ? moment
          .utc(incidentReviewObj?.reviewDate, "YYYY-MM-DD HH:mm:ss UTC")
          .local()
          .format("MM/DD/YYYY")
      : "";
    return `Reviewed [Approved]: ${time}`;
  } else {
    let time = incidentReviewObj?.statusDate
      ? moment
          .utc(incidentReviewObj?.statusDate, "YYYY-MM-DD HH:mm:ss UTC")
          .local()
          .format("MM/DD/YYYY")
      : "";
    return `Status [${incidentReviewObj?.reviewStatus?.name}]: ${time}`;
  }
};

export const formatEvidenceLocationName = evidenceLocation => {
  if (!evidenceLocation) {
    return "";
  } else {
    let location = evidenceLocation?.location ? evidenceLocation.location : "";
    let subLocation = evidenceLocation?.subLocation
      ? evidenceLocation.subLocation
      : "";
    return `${location} - ${subLocation}`;
  }
};

export const handleUuid = () => {
  let uuid = "Offline_" + uuidv4();
  return uuid;
};

export const removeClearedOfficers = incidentsArr => {
  const incidents = Array.isArray(incidentsArr) ? incidentsArr : [];
  return incidents.map(incident => {
    let localIncident = JSON.parse(JSON.stringify(incident));
    const clearedOfficersRemoved =
      localIncident.officerIncidentSpecifics?.filter(
        ois => ois.clearedAt === null,
      ) || [];
    localIncident.officerIncidentSpecifics = clearedOfficersRemoved;
    return localIncident;
  });
};

export const checkForEmptyPropertyObject = obj => {
  for (const key in obj) {
    if (
      obj[key] !== null &&
      obj[key] !== "" &&
      obj[key] !== undefined &&
      obj[key] !== false &&
      obj[key]?.length !== 0
    ) {
      if (
        key !== "id" &&
        key !== "__typename" &&
        key !== "uuid" &&
        key !== "incidentOffenses"
      ) {
        if (obj[key].length > 0) {
          obj[key].map(x => checkForEmptyPropertyObject(x));
        } else {
          return false;
        }
      }
    }
  }
  return true;
};

export const filterEmptyAlerts = (arr = []) => {
  if (Array.isArray(arr)) {
    return arr?.filter(a => !!a?.alertType || !!a?.expireAt || !!a?.officerId);
  } else {
    return [];
  }
};
export const ticketPanelHeader = ticketsArr => {
  if (!ticketsArr || ticketsArr?.length === 0) return "";
  let seen = [];
  let text = "";
  ticketsArr.forEach(t => {
    const name =
      t?.ticketViolationPrototype?.name ||
      t?.ticketViolationPrototype?.fullName;
    if (!seen.includes(t?.ticketViolationPrototype?.id) && name) {
      seen.push(t?.ticketViolationPrototype?.id);
      let count = ticketsArr.filter(
        i =>
          i?.ticketViolationPrototype?.id === t?.ticketViolationPrototype?.id,
      ).length;
      text = text + `${count} ${name}, `;
    }
  });
  //remove last space and comma
  text = text.substring(0, text.length - 2);
  return text;
};
export const objectValueByString = (o, s) => {
  if (s) {
    s = s.replace(/\[(\w+)\]/g, ".$1"); // convert indexes to properties
    s = s.replace(/^\./, ""); // strip a leading dot
    var a = s.split(".");
    for (var i = 0, n = a.length; i < n; ++i) {
      var k = a[i];
      if (!!o && typeof o === "object" && k in o) {
        o = o[k];
      } else {
        return;
      }
    }
    return o;
  } else {
    return;
  }
};

export const convertDateTimeToDateString = str => {
  if (str.includes("UTC")) {
    return moment(str, "YYYY-MM-DD HH:mm:ss").format("MM/DD/YYYY");
  } else {
    return str;
  }
};

export const convertMomentTimeToDateString = momentObj => {
  if (!moment.isMoment(momentObj)) return;
  return momentObj.utc().format("YYYY-MM-DD HH:mm:ss UTC");
};

export const convertDateTimeToMomentObj = str => {
  if (!str) return "";
  let time = moment(str);
  if (time.isValid()) {
    return time;
  } else if (str.includes("UTC")) {
    return moment(str, "YYYY-MM-DD HH:mm:ss");
  } else {
    return str;
  }
};

export const nowServerFormat = () => {
  return moment.utc().format("YYYY-MM-DD HH:mm:ss UTC");
};

export function parseDateFlexible(value) {
  if (value == null || value === "") return null;

  // Case 3: Numeric unix timestamp (seconds or ms)
  if (typeof value === "number" || /^\d+$/.test(value)) {
    // If it's 13 digits, treat as ms; if 10, treat as seconds
    const num = Number(value);
    if (num > 1e12) return moment(num); // ms
    if (num > 1e9) return moment.unix(num); // seconds
  }

  // Case 1: ISO 8601
  if (moment(value, moment.ISO_8601, true).isValid()) {
    return moment(value);
  }

  // Case 2: "YYYY-MM-DD HH:mm:ss UTC"
  if (
    typeof value === "string" &&
    value.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2} UTC$/)
  ) {
    return moment.utc(value, "YYYY-MM-DD HH:mm:ss UTC");
  }

  // Fallback: try parsing as a date string
  const m = moment(value);
  return m.isValid() ? m : null;
}

export const parsePhoneObjToString = phoneObj => {
  if (
    phoneObj.areaCode === "" &&
    phoneObj.prefix === "" &&
    phoneObj.lineCode === "" &&
    phoneObj.ext === ""
  ) {
    return "";
  } else {
    return `${phoneObj.areaCode}-${phoneObj.prefix}-${phoneObj.lineCode}${
      phoneObj.ext ? ` #${phoneObj.ext}` : ""
    }`;
  }
};

function getLocalStorageSizes() {
  const sizes = [];
  let totalSize = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    const compressedItem = localStorage.getItem(key);
    // let item = localGetData(key)?.toString();
    const size = (((compressedItem?.length || 0) + key?.length) * 2) / 1024; // size in kB
    sizes.push({ key, size });
    totalSize += size;
  }
  //sort by size
  sizes.sort((a, b) => b.size - a.size);
  return { sizes, totalSize };
}

async function storageEstimate() {
  try {
    const { usage, quota } = await navigator.storage.estimate();
    return { usage, quota, error: null };
  } catch (error) {
    console.error(error);
    return { usage: null, quota: null, error: error };
  }
}

const convertToMB = bytes => {
  return (bytes / 1024 / 1024).toFixed(2);
};

async function getPermission(permission) {
  try {
    const result = await navigator.permissions.query({ name: permission });
    return `${permission}: ${result.state}`;
  } catch (error) {
    return `${permission} (not supported)`;
  }
}

async function storageAvailable(type) {
  let storage;
  try {
    storage = window[type];
    const x = "__storage_test__";
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      e.name === "QuotaExceededError" &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    );
  }
}

export const localStoreData = async (data, name, addTimestamp = true) => {
  let now = moment();
  let localCopy = JSON.parse(JSON.stringify(data));
  if (
    typeof localCopy === "object" &&
    !Array.isArray(localCopy) &&
    localCopy !== null &&
    addTimestamp
  ) {
    localCopy.timestamp = now;
  }
  let strData = JSON.stringify(localCopy);
  let compressedData;
  if (strData !== undefined && window.LZString !== undefined) {
    try {
      compressedData = window.LZString.compress(strData);
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);
    }
  }

  try {
    if (compressedData !== undefined && compressedData !== null) {
      window.localStorage.setItem(name, compressedData);
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === "QuotaExceededError") {
      const { buildDate: currentVersionDate } = getBuildMeta();
      const { sizes, totalSize } = getLocalStorageSizes();
      let message = `Please screenshot this message and send to support@crosswind.com.\nLocal storage is full. Total in local storage: ${totalSize?.toFixed(
        2,
      )} kB.`;
      const storageResult = await storageEstimate();
      const storagePermission = await getPermission("storage-access");
      const persistentStorage = await getPermission("persistent-storage");
      const localStorageAllowed = await storageAvailable("localStorage");
      message += `\nStorage error: ${storageResult.error}`;
      message += `\nTotal memory: ${convertToMB(
        storageResult.usage,
      )} MB out of ${convertToMB(storageResult.quota)} MB used.`;
      message += `\n${storagePermission}`;
      message += `\n${persistentStorage}`;
      message += `\nLocal Storage is accessible: ${localStorageAllowed}`;
      message += sizes
        .map(s => `\n${s.key}: ${s.size?.toFixed(2)} kB`)
        .join("");
      alert(message);
      Sentry.captureException(message + `Build: ${currentVersionDate}`);
    } else {
      console.error(error);
      Sentry.captureException(error);
      throw error;
    }
  }
  return localCopy;
};

export const localGetData = (name, hours = null, onlineState = false) => {
  if (typeof window === "undefined" || !window?.localStorage?.getItem(name)) {
    return null;
  }

  if (window.localStorage.getItem(name)) {
    const result = localStorage.getItem(name);
    let decompressedData;

    const regexCompressed = /[^\x20-\x7E]/;
    const isCompressed = regexCompressed.test(result);

    if (isCompressed && result && window?.LZString !== undefined) {
      const attemptedDecompressedData = window.LZString.decompress(result);
      if (attemptedDecompressedData) {
        decompressedData = attemptedDecompressedData;
      } else {
        decompressedData = result;
      }
    } else {
      decompressedData = result;
    }

    let parsedData = null;

    try {
      parsedData = JSON.parse(decompressedData);
    } catch (error) {
      console.log(
        "localGetData: You appear to have uncompressed data stored in Local Storage. Please use the Clear Cache button in the menu.",
        name,
      );
      parsedData = decompressedData;
    }
    const now = moment();
    const age = now.diff(parsedData?.timestamp, "hours");
    if (!!hours && age > hours && onlineState) {
      localStorage.removeItem(name);
      return null;
    } else {
      return parsedData;
    }
  } else {
    return null;
  }
};
//adding this for debugging otherwise the local storage can't be viewed decompressed
window.localGetData = localGetData;

export const storeSpecificSetup = (data, name, oriId) => {
  let now = moment();

  let localCopy = JSON.parse(JSON.stringify(data));
  localCopy.timestamp = now;
  localCopy.oriId = oriId;
  localStoreData(localCopy, name);
  return localCopy;
};

export const hasHazard = alertArr => {
  let hazard = false;
  if (alertArr && alertArr?.length > 0) {
    alertArr?.forEach(alert => {
      if (getAlertTypeLevel(alert?.alertType) === 3) {
        hazard = true;
      }
    });
  }
  return hazard;
};

export const pisHasHazard = piss => {
  let hazard = false;
  if (piss && piss.length > 0) {
    piss.forEach(pis => {
      if (
        pis?.person?.validPersonAlerts &&
        pis?.person?.validPersonAlerts.length > 0
      ) {
        pis.person.validPersonAlerts.forEach(alert => {
          if (getAlertTypeLevel(alert?.alertType) === 3) {
            hazard = true;
          }
        });
      }
    });
  }
  return hazard;
};

export const colorHash = {
  0: "",
  1: "green",
  2: "orange",
  3: "red",
};

const incidentColorHash = {
  0: "",
  1: "green",
  2: "orange",
  3: "red",
};

const incidentLinkColorHash = {
  0: "#6096d1",
  1: "green",
  2: "orange",
  3: "red",
};

const normalizeAlertLevel = level => {
  const parsedLevel = Number.parseInt(level ?? 0, 10);

  if (Number.isNaN(parsedLevel)) {
    return 0;
  }

  return Math.max(0, Math.min(parsedLevel, 3));
};

const getAlertTypeLevel = alertType =>
  normalizeAlertLevel(alertType?.alertLevel);

const getMaxAlertLevel = (alerts, max = 3) => {
  if (!Array.isArray(alerts) || alerts.length === 0) {
    return 0;
  }

  let result = 0;

  for (const alert of alerts) {
    const level = getAlertTypeLevel(alert?.alertType);

    if (level > result) {
      result = level;
      if (result === max) {
        return result;
      }
    }
  }

  return result;
};

const getPersonMaxAlertLevel = (person, max = 3) => {
  let result = getMaxAlertLevel(person?.validPersonAlerts, max);

  if (result === max) {
    return result;
  }

  const aliases = Array.isArray(person?.aliases) ? person.aliases : [];
  for (const alias of aliases) {
    const aliasLevel = getMaxAlertLevel(alias?.target?.validPersonAlerts, max);

    if (aliasLevel > result) {
      result = aliasLevel;
      if (result === max) {
        return result;
      }
    }
  }

  return result;
};

export const incidentAlertColor = (
  incident,
  checkPeople = true,
  checkAddress = true,
  link = true,
) => {
  let result = 0;
  const addressMaxAlert = 3;
  const peopleMaxAlert = 3;
  let piss = incident?.personIncidentSpecifics || null;
  if (checkPeople && piss && piss.length > 0) {
    piss.forEach(pis => {
      const level = getPersonMaxAlertLevel(pis?.person, peopleMaxAlert);

      if (level > result) {
        result = level;
      }
    });
  }
  if (
    result < 3 &&
    checkAddress &&
    incident?.address?.validAddressAlerts &&
    incident?.address?.validAddressAlerts.length > 0
  ) {
    result = Math.max(
      result,
      getMaxAlertLevel(incident?.address?.validAddressAlerts, addressMaxAlert),
    );
  }
  return link ? incidentLinkColorHash[result] : incidentColorHash[result];
};

export const personAlertColor = person => {
  return colorHash[getPersonMaxAlertLevel(person)];
};

export const addressAlertColor = address => {
  const max = 3;
  return colorHash[getMaxAlertLevel(address?.validAddressAlerts, max)];
};

export const vehicleAlertColor = vehicle => {
  const max = 3;
  return colorHash[getMaxAlertLevel(vehicle?.validVehicleAlerts, max)];
};

export const createHazardTextArray = (input, type) => {
  let results = [];
  if (input && type === "address") {
    results = input?.validAddressAlerts
      ?.filter(alert => getAlertTypeLevel(alert?.alertType) === 3)
      .map(
        hazard =>
          `${hazard?.alertType?.name}: ${hazard?.notes} [${input?.name}]; `,
      );
    return results ? results : [];
  } else if (type === "person") {
    return results;
  } else if (input && type === "vehicle") {
    return results;
  } else if (input && type === "pis") {
    input.forEach(pis => {
      const person = pis?.person;
      const personName = `${person?.personalAttribute?.lastName}, ${person?.personalAttribute?.firstName}`;

      (person?.validPersonAlerts || [])
        .filter(alert => getAlertTypeLevel(alert?.alertType) === 3)
        .forEach(
          dangerAlert =>
            results.push(
              `${dangerAlert?.alertType?.name}: ${dangerAlert?.notes} [${personName}]; `,
            ),
        );

      (person?.aliases || []).forEach(alias => {
        const aliasName = formatNameFromPersonObj(alias?.target) || "Unknown alias";
        (alias?.target?.validPersonAlerts || [])
          .filter(alert => getAlertTypeLevel(alert?.alertType) === 3)
          .forEach(dangerAlert =>
            results.push(
              `${dangerAlert?.alertType?.name}: ${dangerAlert?.notes} [${personName} (${aliasName})]; `,
            ),
          );
      });
    });
    return results;
  } else {
    return results;
  }
};

export const findPrimaryOfficer = ois => {
  if (ois && ois.length > 0) {
    return ois.find(ois => ois.officer && ois.primaryOfficer)?.officer;
  } else {
    return null;
  }
};

export const returnPeopleInIncident = piss => {
  let persons = [];
  if (piss?.length > 0) {
    piss?.map(pis => {
      let person = JSON.parse(JSON.stringify(pis?.person));
      if (person) {
        person.name = `${pis?.person?.personalAttribute?.lastName}, ${pis?.person?.personalAttribute?.firstName}`;
        persons.push(person);
      }
      return null;
    });
  }
  return persons;
};

export const returnOfficersInIncident = oiss => {
  let officers = [];
  if (oiss?.length > 0) {
    oiss?.map(ois => {
      if (ois?.officer?.id) {
        let officer = {
          id: ois?.officer?.id,
          name: `${ois?.officer?.deptAttribute?.ori?.abbreviation}-${ois?.officer?.deptAttribute?.badge}: ${ois?.officer?.personalAttribute?.lastName}, ${ois?.officer?.personalAttribute?.firstName}`,
        };
        officers.push(officer);
        return null;
      }
      return null;
    });
  }
  return officers;
};

export const ticketStatusHash = {
  1: "In process",
  2: "Validated",
  3: "Printed",
  4: "Submitted",
  5: "Submitted",
  6: "Submitted",
};

export const clearAllCookies = () => {
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
  }
};

export const checkLocalStorageAge = (name, maxAge) => {
  let item = localGetData(name);
  if (item) {
    const now = moment();
    const age = now.diff(item?.timestamp, "hours");
    if (age > maxAge) {
      localStorage.removeItem(name);
      return false;
    } else {
      return true;
    }
  } else {
    return false;
  }
};

const DL_DIGIT_WEIGHTING = [2, 7, 6, 5, 4, 3, 2];
export const calculateCheckDigit = licenseNumber => {
  let dlArr = [...licenseNumber];
  let sum = 0;
  for (let i = 0; i < 7; i++) {
    sum = sum + +dlArr[i] * DL_DIGIT_WEIGHTING[i];
  }
  const remainder = sum % 11;
  const sub = 11 - remainder;
  const checkDigit = sub === 11 ? "A" : sub === 10 ? 0 : sub;
  return checkDigit;
};

export const isLicenseValid = (origLicenseNumber = "", licenseStateStr) => {
  //both missing is valid
  if (!origLicenseNumber && !licenseStateStr) {
    return { success: true, error: null };
  }
  //either missing is invalid
  if (!origLicenseNumber || !licenseStateStr) {
    const message = !origLicenseNumber
      ? "A PID is required when a state is entered"
      : "State is required when a PID is entered";
    return { success: false, error: message };
  }
  const licenseNumber = origLicenseNumber;
  const isAlphaNumericWithDashes = /^[a-zA-Z0-9-]+$/.test(licenseNumber);
  if (!isAlphaNumericWithDashes) {
    return { success: false, error: "PIDs must be alphanumeric or dashes (-)" };
  }
  //there is no validation for non-VT licenses
  if (licenseStateStr !== "VT") {
    return { success: true, error: null };
  } else {
    const vtRegex = /^[0-9]{7}[0-9AFJKLQRTVXY]$/i; // 7 digits followed by any digit or A,F,J,K,L,Q,R,T,V,X,Y (case-insensitive)
    const isVtValid = vtRegex.test(licenseNumber);
    return {
      success: isVtValid,
      error: isVtValid
        ? null
        : "VT PIDs must have 7 digits followed by a digit or A, F, J, K, L, Q, R, T, V, X, Y",
    };
  }
};

export const isZipValid = (zip, stateAbbr) => {
  //find the state
  // const stateAbbr = passedState?.length === 2 ? passedState : STATE_TO_ABBREVIATION[passedState.toUpperCase()]
  const state = STATE_ZIP_CODES[stateAbbr?.toUpperCase()];
  if (!state) {
    return true;
  } else {
    const removePlusFourZip = zip?.trim()?.slice(0, 5);
    return +removePlusFourZip <= +state.max && +removePlusFourZip >= +state.min;
  }
};

export const STATE_TO_ABBREVIATION = {
  ALABAMA: "AL",
  ALASKA: "AK",
  ARIZONA: "AZ",
  ARKANSAS: "AR",
  CALIFORNIA: "CA",
  COLORADO: "CO",
  CONNECTICUT: "CT",
  DELAWARE: "DE",
  FLORIDA: "FL",
  GEORGIA: "GA",
  HAWAII: "HI",
  IDAHO: "ID",
  ILLINOIS: "IL",
  INDIANA: "IN",
  IOWA: "IA",
  KANSAS: "KS",
  KENTUCKY: "KY",
  LOUISIANA: "LA",
  MAINE: "ME",
  MARYLAND: "MD",
  MASSACHUSETTS: "MA",
  MICHIGAN: "MI",
  MINNESOTA: "MN",
  MISSISSIPPI: "MS",
  MISSOURI: "MO",
  MONTANA: "MT",
  NEBRASKA: "NE",
  NEVADA: "NV",
  "NEW HAMPSHIRE": "NH",
  "NEW JERSEY": "NJ",
  "NEW MEXICO": "NM",
  "NEW YORK": "NY",
  "NORTH CAROLINA": "NC",
  "NORTH DAKOTA": "ND",
  OHIO: "OH",
  OKLAHOMA: "OK",
  OREGON: "OR",
  PENNSYLVANIA: "PA",
  "RHODE ISLAND": "RI",
  "SOUTH CAROLINA": "SC",
  "SOUTH DAKOTA": "SD",
  TENNESSEE: "TN",
  TEXAS: "TX",
  UTAH: "UT",
  VERMONT: "VT",
  VIRGINIA: "VA",
  WASHINGTON: "WA",
  "WEST VIRGINIA": "WV",
  WISCONSIN: "WI",
  WYOMING: "WY",
};

export const isAddressComplete = addr => {
  let res =
    !!addr &&
    (!!addr?.streetName || !!addr?.streetNumber) &&
    !!addr?.state &&
    !!addr?.city;
  if (res) {
    // console.log('Checking the zip code')
    res = isZipValid(addr?.zip, addr?.state);
    // console.log(`The zip code is ${res ? "" : "not "}valid`)
  }
  return res;
};

export const isOfficerStateIdNumberValid = str => {
  return /^[A-Z]\d{4}$/.test(str);
};

export const isTicketWarning = ticketViolationPrototype => {
  return (
    parseInt(ticketViolationPrototype?.waiver) === 0 ||
    (!!ticketViolationPrototype?.ticketEntry?.name &&
      ticketViolationPrototype?.ticketEntry?.name
        ?.toLowerCase()
        .includes("warning")) ||
    !ticketViolationPrototype?.civilViolation ||
    !!ticketViolationPrototype?.municipalOrdinance
  );
};

export const isTicketFw = ticketViolationPrototype => {
  return (
    ticketViolationPrototype?.ticketCategory?.name?.includes(
      "Fish And Wildlife",
    ) ?? false
  );
};

export const isWarningNumberValid = ticketNumber => {
  return /^[W]/.test(ticketNumber);
};
export const isTicketNumberValid = ticketNumber => {
  return /^[7]\d{6}$/.test(ticketNumber);
};

const AGE_TIME_ZONE = "America/New_York";

const dateTimeInAgeTimeZone = value => {
  const date = value.toDate();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: AGE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(date)
    .reduce((memo, part) => {
      memo[part.type] = part.value;
      return memo;
    }, {});

  return moment(
    `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`,
    "YYYY-MM-DD HH:mm:ss",
    true,
  );
};

const parseAgeDob = value => {
  const dateOnlyMatch =
    typeof value === "string" ? value.match(/^(\d{4}-\d{2}-\d{2})/) : null;
  if (dateOnlyMatch) {
    return moment(dateOnlyMatch[1], "YYYY-MM-DD", true).startOf("day");
  }

  const parsed = parseDateFlexible(value);
  return parsed?.isValid?.()
    ? dateTimeInAgeTimeZone(parsed).startOf("day")
    : null;
};

const parseAgeCompareDate = value => {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return moment(value, "YYYY-MM-DD", true).startOf("day");
  }

  const parsed = parseDateFlexible(value);
  return parsed?.isValid?.() ? dateTimeInAgeTimeZone(parsed) : null;
};

export const getAgeInYears = (dob, compareDate) => {
  if (!dob || !compareDate) return null;

  const dobMoment = parseAgeDob(dob);
  const compareMoment = parseAgeCompareDate(compareDate);

  if (!dobMoment?.isValid?.() || !compareMoment?.isValid?.()) return null;
  if (compareMoment.isBefore(dobMoment)) return -1;

  return compareMoment.diff(dobMoment, "years", false);
};

export const isJuvenile = (dob, compareDate) => {
  const age = getAgeInYears(dob, compareDate);
  return age !== null && age >= 0 && age < 18;
};

export const isYoeAge = (dob, compareDate) => {
  const age = getAgeInYears(dob, compareDate);
  return age !== null && age < 22 && age > 17;
};

export function sortByOriName(list) {
  let arr = list?.sort(function (a, b) {
    var nameA = a?.oriName?.toUpperCase(); // ignore upper and lowercase
    var nameB = b?.oriName?.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
  return arr;
}

export const updateLogCode = (ois, currentStatus, logCodes) => {
  let logCode = logCodes?.find(
    lc => lc?.id?.toString() === ois?.logCodeId?.toString(),
  );
  if (currentStatus === "dispatchedAt") {
    if (!ois?.enrouteAt && !ois?.onSceneAt && !ois?.clearedAt) {
      logCode = logCodes?.find(lc => lc.name.trim() === "D/Dispatch Unit");
    }
  }
  if (currentStatus === "enrouteAt") {
    if (!ois?.onSceneAt && !ois?.clearedAt) {
      logCode = logCodes?.find(lc => lc.name.trim() === "76/EnRoute");
    }
  }
  if (currentStatus === "onSceneAt") {
    if (!ois?.clearedAt) {
      logCode = logCodes?.find(lc => lc.name.trim() === "23/On Scene");
    }
  }
  if (currentStatus === "clearedAt") {
    logCode = logCodes?.find(lc => lc.name.trim() === "24/Cleared Call");
  }
  if (currentStatus === "S/Secondary Location") {
    logCode = logCodes?.find(lc => lc.name.trim() === "S/Secondary Location");
  }
  if (currentStatus === "89/Enroute to Hospital") {
    logCode = logCodes?.find(lc => lc.name.trim() === "89/Enroute to Hospital");
  }
  if (currentStatus === "90/At Hospital") {
    logCode = logCodes?.find(lc => lc.name.trim() === "90/At Hospital");
  }

  return logCode;
};

export const updateOfficerTimesInOis = (
  ois,
  status,
  date = null,
  officer = null,
  logCodes,
  incidentDispatch,
  activeIncidentDispatch,
  incidentId,
  setStatusMutation,
  userId,
  setOfficerIncidentTimeMutation,
  addOrUpdateOfficerInIncidentMutation,
  setAvailOfficer = null,
  availableOfficerDispatch = null,
) => {
  let localOis = JSON.parse(JSON.stringify(ois));
  const serverTimeFormat =
    date && date?.isValid() ? date.utc().format("YYYY-MM-DD HH:mm:ss UTC") : "";
  const callLogEntryText =
    date && date?.isValid()
      ? date.clone().local().format("YYYY-MM-DD HH:mm:ss")
      : "";
  let logCode = updateLogCode(ois, status, logCodes);
  localOis.logCodeId = logCode?.id;
  localOis.logCode = logCode;
  localOis.currentOfficerStatus = logCode?.name;
  localOis.lastContactedAt = nowServerFormat();
  let input = {
    originatorId: userId,
    officerIncidentSpecificId: ois.id,
  };
  let dispatchedCode = logCodes?.find(lc => lc.name.startsWith("D/"));
  let enrouteCode = logCodes?.find(lc => lc.name.startsWith("76/"));
  let onSceneCode = logCodes?.find(lc => lc.name.startsWith("23/"));
  let backfillStatus = [];
  const shouldUpdateExistingCallLog =
    !!callLogEntryText &&
    ois?.logCodeId?.toString() === localOis?.logCodeId?.toString();
  if (officer) {
    localOis.officer = JSON.parse(JSON.stringify(officer));
  }

  // D/Dispatch Unit
  if (status === "dispatchedAt") {
    localOis.dispatchedAt = serverTimeFormat;
    input.dispatchedAt = serverTimeFormat;
  }
  //76/EnRoute
  if (status === "enrouteAt") {
    localOis.enrouteAt = serverTimeFormat;
    input.enrouteAt = serverTimeFormat;

    if (!localOis.dispatchedAt) {
      backfillStatus.push(dispatchedCode);
      localOis.dispatchedAt = serverTimeFormat;
      input.dispatchedAt = serverTimeFormat;
    }
  }
  //23/On Scene
  if (status === "onSceneAt") {
    localOis.onSceneAt = serverTimeFormat;
    input.onSceneAt = serverTimeFormat;
    if (!localOis.dispatchedAt) {
      backfillStatus.push(dispatchedCode);
      localOis.dispatchedAt = serverTimeFormat;
      input.dispatchedAt = serverTimeFormat;
    }
    if (!localOis.enrouteAt) {
      backfillStatus.push(enrouteCode);
      localOis.enrouteAt = serverTimeFormat;
      input.enrouteAt = serverTimeFormat;
    }
  }
  //24/Cleared Call
  if (status === "clearedAt") {
    localOis.clearedAt = serverTimeFormat;
    input.clearedAt = serverTimeFormat;

    if (!localOis.dispatchedAt) {
      backfillStatus.push(dispatchedCode);
      localOis.dispatchedAt = serverTimeFormat;
      input.dispatchedAt = serverTimeFormat;
    }
    if (!localOis.enrouteAt) {
      backfillStatus.push(enrouteCode);
      localOis.enrouteAt = serverTimeFormat;
      input.enrouteAt = serverTimeFormat;
    }
    if (!localOis.onSceneAt) {
      backfillStatus.push(onSceneCode);
      localOis.onSceneAt = serverTimeFormat;
      input.onSceneAt = serverTimeFormat;
    }
  }
  !!incidentDispatch &&
    incidentDispatch({
      type: "update-ois-multi-field",
      payload: { incidentId: incidentId, oisId: ois?.id, updatedOis: localOis },
    });
  !!activeIncidentDispatch &&
    activeIncidentDispatch({
      type: "update-ois-dash-multi-field",
      payload: { incidentId: incidentId, oisId: ois?.id, updatedOis: localOis },
    });

  if (
    !!addOrUpdateOfficerInIncidentMutation &&
    !!setOfficerIncidentTimeMutation
  ) {
    !!addOrUpdateOfficerInIncidentMutation &&
      officer?.id &&
      addOrUpdateOfficerInIncidentMutation({
        variables: {
          input: {
            incidentId: incidentId,
            officerId: officer?.id,
            originatorId: userId,
            officerIncidentSpecificId: ois.id ? ois.id : ois.uuid,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      }).then(res => {
        const officerIncidentSpecificId =
          res?.data?.addOrUpdateOfficerInIncident?.officerIncidentSpecific?.id;

        if (!officerIncidentSpecificId) {
          Sentry.captureException(
            new Error(
              "updateOfficerTimesInOis received an invalid addOrUpdateOfficerInIncident response",
            ),
            {
              extra: {
                incidentId,
                officerId: officer?.id,
                oisId: ois?.id ?? ois?.uuid,
                response: res,
              },
            },
          );
          return;
        }

        input.officerIncidentSpecificId = officerIncidentSpecificId;
        !!setOfficerIncidentTimeMutation &&
          setOfficerIncidentTimeMutation({
            variables: {
              input: {
                ...input,
                shouldUpdateCallLog: shouldUpdateExistingCallLog,
                callLogEntryText: callLogEntryText,
              },
            },
            context: {
              serializationKey: "incidentQueue",
            },
          });
        if (
          setStatusMutation &&
          ois?.logCodeId?.toString() !== localOis?.logCodeId?.toString()
        ) {
          if (backfillStatus.length > 0) {
            backfillStatus.forEach(stat => {
              setStatusMutation({
                variables: {
                  input: {
                    originatorId: userId,
                    officerId: officer?.id,
                    officerIncidentSpecificId: ois.id,
                    logCodeId: stat?.id,
                    statusCode: stat?.name?.split("/")?.[0],
                    entryText: callLogEntryText,
                  },
                },
                context: { serializationKey: "incidentQueue" },
              });
            });
          }
          setStatusMutation({
            variables: {
              input: {
                originatorId: userId,
                officerId: officer?.id,
                officerIncidentSpecificId: input.officerIncidentSpecificId,
                logCodeId: localOis?.logCodeId,
                statusCode: localOis?.logCode?.name?.split("/")?.[0],
                entryText: callLogEntryText,
              },
            },
            context: { serializationKey: "incidentQueue" },
          }).then(res => {
            const resTenCode =
              res?.data?.setStatus?.officer?.deptAttribute?.status;
            !!availableOfficerDispatch &&
              availableOfficerDispatch({
                type: "update-available-officer",
                payload: {
                  officerId: ois?.officer?.id,
                  property: "status",
                  value: resTenCode,
                },
              });
          });
        }
      });
  } else {
    !!addOrUpdateOfficerInIncidentMutation &&
      addOrUpdateOfficerInIncidentMutation({
        variables: {
          input: {
            incidentId: incidentId,
            officerId: officer?.id,
            originatorId: userId,
            officerIncidentSpecificId: ois.id ? ois.id : ois.uuid,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    !!setOfficerIncidentTimeMutation &&
      setOfficerIncidentTimeMutation({
        variables: {
          input: {
            ...input,
            shouldUpdateCallLog: shouldUpdateExistingCallLog,
            callLogEntryText: callLogEntryText,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    if (
      setStatusMutation &&
      ois?.logCodeId?.toString() !== localOis?.logCodeId?.toString()
    ) {
      //add code here to determine the number of times skipped
      if (backfillStatus.length > 0) {
        backfillStatus.forEach(stat => {
          setStatusMutation({
            variables: {
              input: {
                originatorId: userId,
                officerId: officer?.id,
                officerIncidentSpecificId: ois.id,
                logCodeId: stat?.id,
                statusCode: stat?.name?.split("/")?.[0],
                entryText: callLogEntryText,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
        });
      }
      setStatusMutation({
        variables: {
          input: {
            originatorId: userId,
            officerId: officer?.id,
            officerIncidentSpecificId: ois.id,
            logCodeId: localOis?.logCodeId,
            statusCode: localOis?.logCode?.name?.split("/")?.[0],
            entryText: callLogEntryText,
          },
        },
        context: { serializationKey: "incidentQueue" },
      }).then(res => {
        const resTenCode = res?.data?.setStatus?.officer?.deptAttribute?.status;
        !!availableOfficerDispatch &&
          availableOfficerDispatch({
            type: "update-available-officer",
            payload: {
              officerId: ois?.officer?.id,
              property: "status",
              value: resTenCode,
            },
          });
      });
    }
  }

  // use this to clear the ofc location
  if (
    status === "clearedAt" &&
    !!setAvailOfficer &&
    !!availableOfficerDispatch &&
    ois?.officer?.id
  ) {
    setAvailOfficer({
      variables: {
        input: {
          originatorId: userId,
          officerId: ois?.officer?.id,
          secondaryAddress: "",
        },
      },
    });
    availableOfficerDispatch({
      type: "update-available-officer",
      payload: {
        officerId: ois?.officer?.id,
        property: "officerLocation",
        value: "",
      },
    });
  }
  return localOis;
};

export function displayFedVio(categoryName) {
  return (
    categoryName?.includes("Hazardous Materials") ||
    categoryName === "Motor Carrier Safety Standards"
  );
}
export function displayCommercialFields(categoryName) {
  return Boolean(categoryName?.includes("Weight Restriction"));
}

//returns YYYYMMDD
export function formatDobForQuery(dobString) {
  if (!dobString) {
    return null;
  } else {
    return dobString.split(" ")[0].split("-").join("");
  }
}
// Canadian Provinces
// Alberta	AB
// British Columbia	BC
// Manitoba	MB
// New Brunswick	NB
// Newfoundland and Labrador	NL
// Northwest Territories	NT
// Nova Scotia	NS
// Nunavut	NU
// Ontario	ON
// Prince Edward Island	PE
// Quebec	QC
// Saskatchewan	SK
// Yukon	YT
// function pickStateForQuery(person) {
//   const { } = person?.personalAttribute

// }

//Only run an SNQ with name, dob, gender (optional), state (optional, empty string)
export function determinePersonQuery(person) {
  let resultObj = {
    errors: [],
    queryKey: "SNQ",
  };

  const {
    lastName,
    firstName,
    middleName,
    dob,
    licenseState,
    homeAddress,
    gender,
  } = person?.personalAttribute || {};
  if (!lastName) {
    resultObj.errors.push("Last name missing");
  }
  if (!firstName) {
    resultObj.errors.push("First name missing");
  }
  if (!dob) {
    resultObj.errors.push("Date of birth missing");
  }
  resultObj.NAM = formatName(firstName, middleName, lastName)?.trim();
  resultObj.DOB = formatDobForQuery(dob);
  resultObj.SEX =
    gender?.name === "Male - M"
      ? "M"
      : gender?.name === "Female - F"
        ? "F"
        : "";
  if (licenseState) {
    resultObj.DST = licenseState?.name;
  } else {
    resultObj.DST = homeAddress?.state || "";
  }
  return resultObj;
}

export function isVisEmpty(vis) {
  const {
    vehicleMake,
    vehicleModel,
    owner,
    vehicleColor,
    description,
    vehiclePlate,
    value,
  } = vis?.vehicle || {};
  const rval =
    !vehicleMake &&
    !vehicleModel &&
    !owner &&
    !vehicleColor &&
    !description &&
    !vehiclePlate &&
    !value;
  return rval;
}

export const determineVehicleQuery = (
  vehicle,
  vinOnly = false,
  plateOnly = false,
) => {
  let resultObj = {
    errors: [],
    queryKey: "SVQ",
  };
  const { vehicleVin, vehiclePlate, plateType, vehicleState } = vehicle || {};

  resultObj.DST = vehicleState?.name || "";
  if ((vehicleVin?.name || vinOnly) && !plateOnly) {
    resultObj.VIN = vehicleVin?.name;
    if (vinOnly && !resultObj.VIN) {
      resultObj.errors.push(
        "VIN plate type is required. Please update the vehicle record and try again.",
      );
    }
  } else {
    //regex to find the two letter license code needed for the query
    const regex = /\b([A-Z]{1,2})\b/;
    const licenseCode = plateType?.name.match(regex)?.[1];

    if (licenseCode) {
      //search the plateTypeToLicenseType name's key for a match to licenseCode
      const isSupportedLicenseType = allowedLIT.includes(licenseCode);
      if (isSupportedLicenseType) {
        resultObj.LIT = licenseCode;
      } else {
        resultObj.errors.push(
          `${licenseCode} is not a supported license type. Please update the vehicle record and try again.`,
        );
      }
    } else {
      resultObj.errors.push(
        "License plate type is required. Please update the vehicle record and try again.",
      );
    }
    resultObj.LIY = moment().local().format("YYYY");
    if (!vehiclePlate?.name) {
      resultObj.errors.push("License plate required if not using a VIN");
    } else {
      resultObj.LIC = vehiclePlate.name;
    }
    //iterate over resultObj keys and remove any that are falsey
    const keys = Object.keys(resultObj);
    keys.forEach(key => {
      if (!resultObj[key] && key !== "errors") {
        delete resultObj[key];
      }
    });
  }
  return resultObj;
};

export const isMunicipalVio = violation => {
  return violation?.oriId;
};

export function isValidDateString(dateString) {
  // Regular expressions to match valid date string formats
  const dateRegex1 = /^(\d{4})-(\d{2})-(\d{2})$/;
  const dateRegex2 = /^(\d{4})-(\d{2})-(\d{2})\s(\d{2}):(\d{2}):(\d{2})\sUTC$/;
  let year, month, day;

  // Check if the date string matches the expected formats
  if (dateRegex1.test(dateString)) {
    [, year, month, day] = dateRegex1.exec(dateString);
  } else if (dateRegex2.test(dateString)) {
    [, year, month, day] = dateRegex2.exec(dateString);
  } else {
    return false;
  }

  // Create a Date object from the extracted values
  const date = new Date(year, month - 1, day);

  // Check if the date is valid
  if (
    date.getFullYear() !== parseInt(year, 10) ||
    date.getMonth() + 1 !== parseInt(month, 10) ||
    date.getDate() !== parseInt(day, 10)
  ) {
    return false;
  }

  // Get the current date and calculate the minimum allowed date (100 years ago)
  const currentDate = new Date();
  const minAllowedDate = new Date();
  minAllowedDate.setFullYear(currentDate.getFullYear() - 100);

  // Check if the date is not in the future and not over 100 years old
  if (date > currentDate || date < minAllowedDate) {
    return false;
  }

  // All conditions are met
  return true;
}

const LOCKED_PERSONAL_ATTRIBUTE_LOOK_UP = {
  firstName: "lockedFirstName",
  lastName: "lockedLastName",
  middleName: "lockedMiddleName",
  driversLicense: "lockedLicense",
  fishWildlifeLicense: "lockedFishWildlifeLicense",
  phone: "lockedPrimaryPhone",
  cell: "lockedCell",
  dob: "lockedDob",
  height: "lockedHeight",
  weight: "lockedWeight",
  pobAddress: "lockedPobAddress",
  gender: "lockedGender",
  hairColor: "lockedHairColor",
  eyeColor: "lockedEyeColor",
  organization: "lockedOrganization",
  mailingAddress: "lockedMailingAddress",
  licenseState: "lockedLicenseState",
};

export const returnLockedTicket = ticket => {
  let res = ticket;
  if (ticket?.currentStatus < 3) return ticket;
  if (ticket?.issuedTo) {
    Object.keys(LOCKED_PERSONAL_ATTRIBUTE_LOOK_UP).forEach(key => {
      res.issuedTo.personalAttribute[key] =
        ticket?.issuedTo?.personalAttribute?.[
          LOCKED_PERSONAL_ATTRIBUTE_LOOK_UP[key]
        ] || ticket.issuedTo.personalAttribute[key];
    });
  }
  return res;
  // if (ticket?.vehicle)
};

export const isSearchBlank = (searchObj, comparisonObj) => {
  return Object.keys(searchObj).every(key => {
    if (typeof searchObj[key] === "object" && searchObj[key] !== null) {
      return Object.keys(searchObj[key]).every(innerKey => {
        return (
          searchObj[key][innerKey] === "" ||
          searchObj[key][innerKey] === comparisonObj[key][innerKey]
        );
      });
    } else {
      return searchObj[key] === "" || searchObj[key] === comparisonObj[key];
    }
  });
};

export const convertMetersToFeet = meters => {
  if (typeof meters !== "number" || isNaN(meters)) {
    // throw new Error("Input must be a valid number");
    return "NaN";
  }
  const feet = meters * 3.28084; // 1 meter = 3.28084 feet
  return parseFloat(feet.toFixed(2)); // Return the result
};

const allowedLIT = [
  "PC",
  "TK",
  "TL",
  "MC",
  "FM",
  "TM",
  "DL",
  "AT",
  "DX",
  "AP",
  "BU",
  "CI",
  "CU",
  "ST",
  "AQ",
  "CN",
];

export const filterBlankAliases = aliases => {
  if (!aliases || !Array.isArray(aliases)) {
    return [];
  }
  return aliases.filter(
    alias =>
      alias?.id && (alias.description || (alias.target && alias.target.id)),
  );
};

//shouldn't run queries
// "Boat - M",
// "Snowmobile - SN",

export const isSoftTokenDisabled = oris => {
  let result = true;
  const firstOri = (oris || []).find(ori => +ori?.id === 1);
  if (firstOri && firstOri?.tokenDisabled !== undefined) {
    result = firstOri?.tokenDisabled;
  }
  return result;
};
export function parseIncidentSubscriptionMessage(message) {
  // Try to parse as JSON
  // console.log("Parsing incident subscription message:", message);
  try {
    if (typeof message === "object") return message;
    return JSON.parse(message);
  } catch (e) {
    // Not JSON, try colon-delimited
    if (typeof message !== "string") return {};
    const tokens = message.split(":");
    // Map tokens to keys in order
    const [incidentId, frame, originatorId, app] = tokens;
    return {
      incidentId: incidentId
        ? isNaN(Number(incidentId))
          ? incidentId
          : Number(incidentId)
        : undefined,
      frame,
      originatorId: originatorId
        ? isNaN(Number(originatorId))
          ? originatorId
          : Number(originatorId)
        : undefined,
      app,
    };
  }
}
export function parseQuerySubscriptionMessage(message) {
  console.log("Parsing query subscription message:", message);
  // Try to parse as JSON
  try {
    if (typeof message === "object") return message;
    return JSON.parse(message);
  } catch (e) {
    // Not JSON, try colon-delimited
    if (typeof message !== "string") return {};
    const tokens = message.split(":");
    // Map tokens to keys in order
    const [type, id, originatorId, app] = tokens;
    return {
      [type]: id ? (isNaN(Number(id)) ? id : Number(id)) : undefined,
      originatorId: originatorId
        ? isNaN(Number(originatorId))
          ? originatorId
          : Number(originatorId)
        : undefined,
      app,
    };
  }
}
export function parseNarrativeSubscriptionMessage(message) {
  // Try to parse as JSON
  try {
    if (typeof message === "object") return message;
    return JSON.parse(message);
  } catch (e) {
    // Not JSON, try colon-delimited
    if (typeof message !== "string") return {};
    const tokens = message.split(":");
    // Map tokens to keys in order
    const [type, id, action, originatorId, source] = tokens;
    return {
      [type]: id ? (isNaN(Number(id)) ? id : Number(id)) : undefined,
      originatorId: originatorId
        ? isNaN(Number(originatorId))
          ? originatorId
          : Number(originatorId)
        : undefined,
      source,
      action,
      app: "",
    };
  }
}

export function getOrCreateClientId() {
  let clientId = localGetData("client_id");
  if (!clientId) {
    clientId = uuidv4();
    localStoreData(clientId, "client_id");
  }
  return clientId;
}

export function pickPreferredCommonCallType(...values) {
  for (const value of values) {
    if (value) {
      return value;
    }
  }
  return "";
}

export const missingLastName = person => {
  if (
    person?.personalAttribute?.firstName ||
    person?.personalAttribute?.middleName ||
    person?.personalAttribute?.suffix
  ) {
    return !person?.personalAttribute?.lastName;
  } else {
    return false;
  }
};
